import { WorksheetGridFieldSection, WorksheetRecordGrid } from 'Components/Worksheets/Core';
import { actions } from 'Data/ReduxViewModels/WorksheetEditor';
import { pipe } from 'Lib/Hooks/Selectors';
import { joinPaths } from 'Lib/Object/Path/shared';
import deepEquals from 'Lib/Object/deepEquals';
import { useDispatch, useSelector } from 'react-redux';
import { LowerBoundResultField } from '.';
import { Checkbox, Field, NumericInput, RadioGroup } from '../../../../Core';
import { NamePrefixModifier, withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { FieldSet, ListFieldSection, SectionGroup } from '../../../../Layout';
import { IWorksheetComponent } from '../../../Core/types';

export interface IMeasurementsSectionBaseProps extends IWorksheetComponent {
  showEmbeddedTestFields?: boolean;
}

let MeasurementsSectionBase = ({
  createWorksheetSelector,
  prefixName,
  showEmbeddedTestFields,
}: IMeasurementsSectionBaseProps): JSX.Element => {
  const resultNotObtainable = useSelector(
    pipe(createWorksheetSelector!('Measurements.ResultNotObtainable'), (rno) => rno ?? false)
  );
  const dispatch = useDispatch();

  const measurementRecordProps = useSelector(
    pipe(createWorksheetSelector!('Measurements.Measurements'), (measurements) =>
      measurements?.map((m: any) => ({
        disableReading: (m.MaximumExceeded ?? false) as boolean,
      }))
    ),
    deepEquals
  );

  //setting ResultNotObtainableReason default value conditionally (adapted from NFD worksheet)
  const handleResultNotObtainable = ({ data: { value: checked } }: any) => {
    dispatch(actions.setValue({ name: prefixName!('Measurements.ResultNotObtainable'), value: checked, sync: true }));
    if (!checked) {
      dispatch(
        actions.setValue({ name: prefixName!('Measurements.ResultNotObtainableReason'), value: null, sync: true })
      );
    } else {
      dispatch(
        actions.setValue({
          name: prefixName!('Measurements.ResultNotObtainableReason'),
          value: 'Unable to penetrate soil',
          sync: true,
        })
      );
    }
  };

  return (
    <NamePrefixModifier appendNamePrefix="Measurements">
      <SectionGroup>
        <ListFieldSection>
          <FieldSet>
            <Field input={<Checkbox />} name="ResultNotObtainable" onChange={handleResultNotObtainable} />
            <Field
              input={<RadioGroup />}
              options={[{ label: 'Unable to penetrate soil', value: 'Unable to penetrate soil' }]}
              freeEntry={true}
              freeEntryLabel="Other"
              defaultValue="Unable to penetrate soil"
              name="ResultNotObtainableReason"
              maxLength={100}
              visible={resultNotObtainable}
            />
          </FieldSet>
        </ListFieldSection>
        <WorksheetGridFieldSection visible={!resultNotObtainable} name="Measurements">
          <WorksheetRecordGrid
            name="Measurements"
            tabDirection={'vertical'}
            orientation="vertical"
            recordProps={{ heading: 'number', preventCreate: true, preventRemove: true }}
            fieldSet={[
              {
                name: 'Reading',
                width: 120,
                component: (i) => (
                  <Field input={<NumericInput />} disabled={measurementRecordProps[i]?.disableReading} />
                ),
              },
              {
                name: 'MaximumExceeded',
                width: 90,
                component: <Field input={<Checkbox />} />,
              },
              {
                name: 'VaneShearStrength',
                width: 100,
                component: (i) => (
                  <LowerBoundResultField isLowerBoundFieldName={joinPaths(i.toString(), 'MaximumExceeded')} />
                ),
              },
              {
                name: 'ResultNotObtainable',
                width: 120,
                component: (i) => (i > 1 ? <Field input={<Checkbox />} /> : <></>),
                hidden: !showEmbeddedTestFields,
              },
            ]}
          />
        </WorksheetGridFieldSection>
      </SectionGroup>
    </NamePrefixModifier>
  );
};

const MeasurementsSection = withInjectedProps(MeasurementsSectionBase);
export { MeasurementsSection };
export default MeasurementsSection;
